import gql from "graphql-tag";
import {useEffect} from "react";

import {FetchPolicy} from "@khanacademy/wonder-blocks-data";
import {useGrowthBookForABTest} from "@repo/analytics-growthbook";
import {useQuery} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {setPromptVariable} from "@repo/guide-core-helpers";
import {useGuideState} from "@repo/guide-state";

import type {AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType} from "./__graphql-types__/index.ts";

export const CONFIG_QUERY: GqlOperation<
    AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType["response"],
    AiGuideActivityConfigGrowthbookFeaturesAsPromptVariablesType["variables"]
> = gqlOp(gql`
    query AiGuideActivityConfigGrowthbookFeaturesAsPromptVariables(
        $threadId: String!
    ) {
        aiGuideActivityConfigByThreadId(threadId: $threadId) {
            growthbookFeaturesAsPromptVariables
        }
    }
`);

export const useGrowthbookFeaturesAsPromptVariables = () => {
    const threadId = useGuideState("threadId");
    const growthBook = useGrowthBookForABTest();

    const [aiGuideActivityConfig] = useQuery(CONFIG_QUERY, {
        variables: {threadId},
        fetchPolicy: FetchPolicy.CacheAndNetwork,
        skip: !threadId,
    });

    useEffect(() => {
        if (!growthBook) {
            return;
        }
        if (!threadId) {
            return;
        }
        if (aiGuideActivityConfig.status !== "success") {
            return;
        }
        const featureKeys =
            aiGuideActivityConfig.data?.aiGuideActivityConfigByThreadId
                ?.growthbookFeaturesAsPromptVariables;
        if (!featureKeys) {
            return;
        }

        // Add threadId context to GrowthBook Attributes.
        growthBook.setAttributes({
            ...growthBook.getAttributes(),
            threadId,
        });

        for (const featureKey of featureKeys) {
            const featureValue = growthBook.getFeatureValueAndEnrollInABTest(
                featureKey,
                "NONE",
            );
            // Transform the feature key using kebab-case to camelCase.
            const safeFeatureKey = featureKey.replace(
                /-([a-z])/g,
                (_, letter) => letter.toUpperCase(),
            );
            setPromptVariable(safeFeatureKey, featureValue);
        }
    }, [threadId, growthBook, aiGuideActivityConfig]);
};
